import React from 'react';
import { motion } from 'framer-motion';
import { CameraIcon, CalendarIcon, MapPinIcon, Trash2Icon, RecycleIcon, FileTextIcon, ChevronRightIcon, BellIcon, MapIcon, ExternalLinkIcon, CheckIcon, ArrowDownIcon, LockIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
type Icon = typeof CameraIcon;
const EASE = [0.23, 1, 0.32, 1] as const;
const PHOTO = {
  container: 'https://cdn.magicpatterns.com/patterns/generated-images/42283f42-4328-471a-814a-38b21dee0cd6.jpg',
  house: 'https://cdn.magicpatterns.com/patterns/generated-images/92bf864b-a413-44e1-8ad7-bd7e0bc37970.jpg',
  batteries: 'https://cdn.magicpatterns.com/patterns/generated-images/4ce35d03-58dc-467b-bd11-36566cf46158.jpg',
  bin: 'https://cdn.magicpatterns.com/patterns/generated-images/2385c45a-2ab2-4a17-8143-b087930a8897.jpg',
  blueBin: 'https://cdn.magicpatterns.com/patterns/generated-images/ad8661f9-03de-4c37-876a-0049be6a562c.jpg'
};
function IconCircle({
  icon: I
}: {
  icon: Icon;
}) {
  return <span className="w-12 h-12 shrink-0 rounded-full bg-[#F4F4FC] flex items-center justify-center" data-id="element-1875" data-mp="brffqf4z5dub">
      <I className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1876" data-mp="gph8jrs7criz" />
    </span>;
}
function Badge({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="inline-block rounded-md bg-[#F1F0FD] px-2 py-1 font-sans text-[10px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5] leading-none" data-id="element-1877" data-mp="pqowbfx3e9yt">
      {children}
    </span>;
}
function QuestionCard({
  icon,
  question,
  hint,
  img,
  alt
}: {
  icon: Icon;
  question: React.ReactNode;
  hint: React.ReactNode;
  img: string;
  alt: string;
}) {
  return <div className="flex items-center gap-4 rounded-2xl border border-[#E7E8F0] bg-white p-4 shadow-[0_2px_10px_rgba(18,18,18,0.03)] min-h-[128px]" data-id="element-1878" data-mp="18fvft55pn5lo">
      <IconCircle icon={icon} data-id="element-1879" data-mp="1dgrixgi8mnq1" />
      <div className="min-w-0 flex-1" data-id="element-1880" data-mp="1ieazin2ercl5">
        <p className="font-sans text-[15.5px] text-[#121212] leading-snug" data-id="element-1881" data-mp="1ifcnfs4ivtka">{question}</p>
        <p className="font-sans text-[13px] text-[#8A90A0] leading-snug mt-2" data-id="element-1882" data-mp="3ego5vltjrdj5">{hint}</p>
      </div>
      <img src={img} alt={alt} loading="lazy" className="w-[88px] h-[84px] shrink-0 rounded-xl object-cover" data-id="element-1883" data-mp="39j4fjnjkraau" />
    </div>;
}
function AnswerHeader({
  icon,
  title,
  badge
}: {
  icon: Icon;
  title: string;
  badge: string;
}) {
  return <div className="flex items-start gap-4" data-id="element-1884" data-mp="3jey301yu0o7e">
      <IconCircle icon={icon} data-id="element-1885" data-mp="129m0yoo329f" />
      <div className="min-w-0 pt-1" data-id="element-1886" data-mp="11i4grrwn525">
        <p className="font-sans text-[16.5px] font-medium text-[#121212] leading-snug" data-id="element-1887" data-mp="14mc5z4kwixk">{title}</p>
        <div className="mt-2" data-id="element-1888" data-mp="3t8eswuvulj5s">
          <Badge data-id="element-1889" data-mp="b0h0navhh889">{badge}</Badge>
        </div>
      </div>
    </div>;
}
function Arrow() {
  return <div className="flex justify-center py-2" aria-hidden="true" data-id="element-1890" data-mp="614fcuk9g97s">
      <ArrowDownIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} data-id="element-1891" data-mp="5zjnjcpbz4dm" />
    </div>;
}
const cardBase = 'flex flex-col h-full rounded-2xl border border-[#E7E8F0] bg-white p-5 shadow-[0_2px_10px_rgba(18,18,18,0.03)]';
const actionBase = 'mt-auto flex items-center gap-3 rounded-xl border px-4 py-3 font-sans text-[13.5px] transition-colors';

/** May 2026 — starts on a Friday (Monday-first grid). */
function MiniCalendar({
  isFR
}: {
  isFR: boolean;
}) {
  const days = isFR ? ['L', 'M', 'M', 'J', 'V', 'S', 'D'] : ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  const cells: (number | null)[] = [null, null, null, null, ...Array.from({
    length: 31
  }, (_, i) => i + 1)];
  const marks: Record<number, string> = {
    14: 'bg-[#121212] text-white',
    21: 'bg-[#4F46E5] text-white',
    28: 'bg-[#8A5A3B] text-white'
  };
  return <div className="rounded-xl border border-[#EDEEF3] p-3" data-id="element-1892" data-mp="3qrx9qrzctcxe">
      <div className="flex items-center justify-between" data-id="element-1893" data-mp="3qti6mo9557yf">
        <span className="font-sans text-[12.5px] font-medium text-[#121212]" data-id="element-1894" data-mp="3vpytzg8iniag">{isFR ? 'Mai 2026' : 'May 2026'}</span>
        <ChevronRightIcon className="w-3.5 h-3.5 text-[#4F46E5]" aria-hidden="true" data-id="element-1895" data-mp="1p1lrl4nrlfyw" />
      </div>
      <div className="grid grid-cols-7 gap-y-1 mt-2 text-center" data-id="element-1896" data-mp="1ty3vy3f6ncap">
        {days.map((d, i) => <span key={i} className="font-sans text-[9px] text-[#9AA0AE]" data-id="element-1897" data-mp="3q18m7fqk3mj5" data-mpi={i}>
            {d}
          </span>)}
        {cells.map((n, i) => <span key={i} className="flex justify-center" data-id="element-1898" data-mp="3h1va0fb1sjuq" data-mpi={i}>
            {n && <span className={`w-[18px] h-[18px] rounded-full flex items-center justify-center font-sans text-[9px] ${marks[n] ?? 'text-[#8A90A0]'}`} data-id="element-1899" data-mp="3h3y87mmctjaj">
                {n}
              </span>}
          </span>)}
      </div>
    </div>;
}
function DropOffMap({
  label
}: {
  label: string;
}) {
  return <div className="relative h-[118px] rounded-xl overflow-hidden border border-[#EDEEF3] bg-[#F3F4F2]" data-id="element-1900" data-mp="2yblvq71zl1mo">
      <svg viewBox="0 0 300 120" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" aria-hidden="true" data-id="element-1901" data-mp="2ta6buibjp15v">
        <path d="M-10 70 C 60 40, 110 110, 180 80 S 280 60, 320 90" stroke="#C9DDEB" strokeWidth="14" fill="none" data-id="element-1902" data-mp="2je38j94yiwhc" />
        <path d="M0 20 L 300 45 M 40 0 L 90 120 M 200 0 L 170 120 M 0 105 L 300 95" stroke="#FFFFFF" strokeWidth="4" fill="none" data-id="element-1903" data-mp="29ao7mvbm6m0j" />
      </svg>
      {[{
      l: '12%',
      t: '58%'
    }, {
      l: '30%',
      t: '48%'
    }, {
      l: '40%',
      t: '80%'
    }, {
      l: '76%',
      t: '56%'
    }].map((p, i) => <MapPinIcon key={i} className="absolute w-5 h-5 -translate-x-1/2 -translate-y-full text-[#4F46E5] fill-[#4F46E5]/20" style={{
      left: p.l,
      top: p.t
    }} strokeWidth={2} aria-hidden="true" data-id="element-1904" data-mp="2o7ypqniriq58" data-mpi={i} />)}
      <div className="absolute right-2 top-2 flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 shadow-[0_2px_8px_rgba(18,18,18,0.08)]" data-id="element-1905" data-mp="1vgf46wyp1002">
        <span className="w-6 h-6 rounded-full bg-[#4F46E5] flex items-center justify-center" data-id="element-1906" data-mp="258t5xppru6m8">
          <MapPinIcon className="w-3.5 h-3.5 text-white" strokeWidth={2} aria-hidden="true" data-id="element-1907" data-mp="17tywtwic40rh" />
        </span>
        <span className="font-sans text-[11px] leading-tight text-[#121212]" data-id="element-1908" data-mp="1cr8h6xqv9m8g">
          {label}
          <span className="block text-[10px] text-[#8A90A0]" data-id="element-1909" data-mp="14qsu0vq0g1rr">2,3 km</span>
        </span>
      </div>
    </div>;
}
export function CollectJourneys() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const t = (fr: string, en: string) => isFR ? fr : en;
  const collections = [{
    color: 'bg-[#3A3A3A]',
    label: t('Ordures', 'Garbage'),
    date: t('Jeudi 14 mai', 'Thursday, May 14')
  }, {
    color: 'bg-[#4F46E5]',
    label: t('Recyclage', 'Recycling'),
    date: t('Jeudi 21 mai', 'Thursday, May 21')
  }, {
    color: 'bg-[#8A5A3B]',
    label: t('Matières organiques', 'Organics'),
    date: t('Jeudi 28 mai', 'Thursday, May 28')
  }];
  const dropoffs = [{
    name: t('Écocentre municipal', 'Municipal ecocentre'),
    km: t('2,3 km', '2.3 km')
  }, {
    name: t('Centre de récupération', 'Recovery centre'),
    km: t('4,1 km', '4.1 km')
  }, {
    name: t('Pharmacie (piles)', 'Pharmacy (batteries)'),
    km: t('5,0 km', '5.0 km')
  }];
  const steps = [{
    done: true,
    label: t('Demande soumise', 'Request submitted'),
    time: t('12 mai 2026, 10 h 42', 'May 12, 2026, 10:42 AM')
  }, {
    done: false,
    label: t('En traitement', 'In progress')
  }, {
    done: false,
    label: t('Intervention planifiée', 'Service scheduled')
  }];
  const journeys: {
    q: React.ReactNode;
    a: React.ReactNode;
  }[] = [{
    q: <QuestionCard icon={CameraIcon} question={t('« Où ça va, ce contenant ? »', '“Where does this container go?”')} hint={t('Photo ou voix', 'Photo or voice')} img={PHOTO.container} alt={t('Un contenant de plastique vide.', 'An empty plastic container.')} data-id="element-1910" data-mp="24s5z62lj9otu" />,
    a: <div className={cardBase} data-id="element-1911" data-mp="2jmuahxdm7hhd">
          <AnswerHeader icon={RecycleIcon} title={t('Recyclage', 'Recycling')} badge={t('Réponse immédiate', 'Instant answer')} data-id="element-1912" data-mp="2npgwhhnlu86n" />
          <div className="flex items-center gap-3 mt-6 mb-6" data-id="element-1913" data-mp="3be9wpqb9f272">
            <div className="min-w-0 flex-1" data-id="element-1914" data-mp="3g7330imdmil9">
              <p className="font-sans text-[16px] font-medium text-[#121212] leading-snug" data-id="element-1915" data-mp="3g7ukkpj52fsj">
                {t('Ce contenant va dans le bac de recyclage.', 'This container goes in the recycling bin.')}
              </p>
              <p className="font-sans text-[13.5px] text-[#8A90A0] leading-relaxed mt-2" data-id="element-1916" data-mp="2mnc98bht5q7n">
                {t('Videz et rincez-le. Laissez le couvercle attaché.', 'Empty and rinse it. Leave the lid attached.')}
              </p>
            </div>
            <img src={PHOTO.blueBin} alt={t('Bac de recyclage bleu.', 'Blue recycling bin.')} loading="lazy" className="w-[92px] h-[92px] shrink-0 object-contain mix-blend-multiply" data-id="element-1917" data-mp="3adq5ke7feb81" />
          </div>
          <span className={`${actionBase} border-[#EDEEF3] text-[#121212]`} data-id="element-1918" data-mp="2vhijf8cgizq4">
            <FileTextIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1919" data-mp="2vjl9tthiuuvd" />
            <span className="flex-1" data-id="element-1920" data-mp="1ozc1hwa3nvs1">{t('Consignes locales', 'Local guidelines')}</span>
            <ChevronRightIcon className="w-4 h-4 text-[#8A90A0]" aria-hidden="true" data-id="element-1921" data-mp="1k10oxdsf7764" />
          </span>
        </div>
  }, {
    q: <QuestionCard icon={CalendarIcon} question={t('« Quand passent les ordures chez moi ? »', '“When is garbage collected at my address?”')} hint={t('Adresse connue ou géolocalisation', 'Known address or geolocation')} img={PHOTO.house} alt={t('Une maison résidentielle.', 'A residential house.')} data-id="element-1922" data-mp="4iupdk34qryv" />,
    a: <div className={cardBase} data-id="element-1923" data-mp="n9voc8492nif">
          <AnswerHeader icon={Trash2Icon} title={t('Prochaines collectes', 'Upcoming collections')} badge={t('Calendrier personnalisé', 'Personalized calendar')} data-id="element-1924" data-mp="n8uajvvunmx0" />
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-3 mt-5 mb-5" data-id="element-1925" data-mp="x6qkwlgyqdu2">
            <ul className="space-y-3.5 pt-1" data-id="element-1926" data-mp="2u1myk7gfpr8t">
              {collections.map(c => <li key={c.label} className="flex items-center gap-2.5" data-id="element-1927" data-mp="2z0hf12qxj3pr">
                  <span className={`w-3.5 h-5 shrink-0 rounded-[3px] ${c.color}`} aria-hidden="true" data-id="element-1928" data-mp="1ehxcpw0q38ki" />
                  <span className="min-w-0" data-id="element-1929" data-mp="1jgigg5f6oadp">
                    <span className="block font-sans text-[13px] text-[#121212] leading-tight" data-id="element-1930" data-mp="1jha7d2xu5jxn">{c.label}</span>
                    <span className="block font-sans text-[12px] text-[#8A90A0] leading-tight mt-0.5" data-id="element-1931" data-mp="1oejycn0vi3nl">{c.date}</span>
                  </span>
                </li>)}
            </ul>
            <MiniCalendar isFR={isFR} data-id="element-1932" data-mp="1xg9mnjzgkmh5" />
          </div>
          <div className="mt-auto grid grid-cols-2 gap-2" data-id="element-1933" data-mp="22f341nhv8xml">
            <span className="flex items-center justify-center gap-2 rounded-xl border border-[#4F46E5] px-2 py-3 font-sans text-[12.5px] text-[#4F46E5] text-center" data-id="element-1934" data-mp="22djmltkh4gj8">
              <CalendarIcon className="w-4 h-4 shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-1935" data-mp="2c44i384up4xx" />
              {t('Ajouter au calendrier', 'Add to calendar')}
            </span>
            <span className="flex items-center justify-center gap-2 rounded-xl border border-[#EDEEF3] px-2 py-3 font-sans text-[12.5px] text-[#121212] text-center" data-id="element-1936" data-mp="2l5cetlrpxxpw">
              <BellIcon className="w-4 h-4 shrink-0" strokeWidth={1.7} aria-hidden="true" data-id="element-1937" data-mp="4jnsg0ksq14w" />
              {t('Activer un rappel', 'Set a reminder')}
            </span>
          </div>
        </div>
  }, {
    q: <QuestionCard icon={MapPinIcon} question={t('« Où déposer mes piles ? »', '“Where can I drop off my batteries?”')} hint={t('Localisation', 'Location')} img={PHOTO.batteries} alt={t('Des piles usagées.', 'Used batteries.')} data-id="element-1938" data-mp="3rp5btl2yijbs" />,
    a: <div className={cardBase} data-id="element-1939" data-mp="jfwqwzwv4ag9">
          <AnswerHeader icon={MapPinIcon} title={t('Points de dépôt', 'Drop-off locations')} badge={t('Près de chez vous', 'Near you')} data-id="element-1940" data-mp="jf3p8dcrgb4z" />
          <div className="mt-5" data-id="element-1941" data-mp="1bwzeqhy5tsfs">
            <DropOffMap label={dropoffs[0].name} data-id="element-1942" data-mp="1by113ikmm1ct" />
          </div>
          <ul className="space-y-2 mt-4 mb-5" data-id="element-1943" data-mp="175zfh61kiya4">
            {dropoffs.map(d => <li key={d.name} className="flex items-center gap-2.5 font-sans text-[13px]" data-id="element-1944" data-mp="1q2rh37z7venk">
                <MapPinIcon className="w-3.5 h-3.5 shrink-0 text-[#4F46E5]" strokeWidth={1.8} aria-hidden="true" data-id="element-1945" data-mp="1v1uq13ibi50t" />
                <span className="flex-1 text-[#121212]" data-id="element-1946" data-mp="1hapjdhgj8cgz">{d.name}</span>
                <span className="text-[#8A90A0]" data-id="element-1947" data-mp="1h8d3d9uc4c6k">{d.km}</span>
              </li>)}
          </ul>
          <span className={`${actionBase} border-[#4F46E5] text-[#4F46E5] justify-center`} data-id="element-1948" data-mp="1l9oob229k98f">
            <MapIcon className="w-4 h-4" strokeWidth={1.7} aria-hidden="true" data-id="element-1949" data-mp="17emkqlkw2vys" />
            <span data-id="element-1950" data-mp="1cco2n36kwp8o">{t('Voir tous les points de dépôt', 'View all drop-off locations')}</span>
            <ChevronRightIcon className="w-4 h-4 ml-auto" aria-hidden="true" data-id="element-1951" data-mp="yd6bih5t8zw5" />
          </span>
        </div>
  }, {
    q: <QuestionCard icon={Trash2Icon} question={t('« Mon bac est brisé. »', '“My bin is broken.”')} hint={t('Identité + adresse', 'Identity + address')} img={PHOTO.bin} alt={t('Un bac municipal endommagé.', 'A damaged municipal bin.')} data-id="element-1952" data-mp="2spmmtwdgr47v" />,
    a: <div className={cardBase} data-id="element-1953" data-mp="h2swietiqsox">
          <AnswerHeader icon={Trash2Icon} title={t('Demande de service', 'Service request')} badge={t('Prise en charge rapide', 'Fast follow-up')} data-id="element-1954" data-mp="h3u8qmm9tl88" />
          <div className="border-t border-[#EDEEF3] mt-5 pt-5 mb-5 flex gap-3" data-id="element-1955" data-mp="q37lpp9lsrxp">
            <ol className="min-w-0 flex-1" data-id="element-1956" data-mp="q14j85r9l6c3">
              {steps.map((s, i) => <li key={s.label} className="relative flex gap-3 pb-5 last:pb-0" data-id="element-1957" data-mp="14q0tm9gsmvfv" data-mpi={i}>
                  {i < steps.length - 1 && <span className="absolute left-[10px] top-6 bottom-0 w-px bg-[#E4E6EC]" aria-hidden="true" data-id="element-1958" data-mp="1ds9zun5142ng" />}
                  {s.done ? <span className="relative w-[21px] h-[21px] shrink-0 rounded-full bg-[#4F46E5] flex items-center justify-center" data-id="element-1959" data-mp="18u8flyvv9xae">
                      <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} aria-hidden="true" data-id="element-1960" data-mp="3al21f3wg7aqu" />
                    </span> : <span className="relative w-[21px] h-[21px] shrink-0 rounded-full border border-[#C9CCD6] bg-white" data-id="element-1961" data-mp="3cyxgjh59fjlr" />}
                  <span className="min-w-0 pt-0.5" data-id="element-1962" data-mp="3m05dnvw1iee9">
                    <span className="block font-sans text-[13px] text-[#121212] leading-tight" data-id="element-1963" data-mp="3m1gepny3p81o">{s.label}</span>
                    {s.time && <span className="block font-sans text-[11.5px] text-[#8A90A0] mt-1" data-id="element-1964" data-mp="3qx5ki90prl6f">{s.time}</span>}
                  </span>
                </li>)}
            </ol>
            <div className="w-[104px] shrink-0 rounded-xl border border-[#EDEEF3] p-1.5" data-id="element-1965" data-mp="2yb405u8cod24">
              <img src={PHOTO.bin} alt={t('Bac endommagé.', 'Damaged bin.')} loading="lazy" className="w-full h-[74px] rounded-lg object-cover" data-id="element-1966" data-mp="338vt8rkqe57w" />
              <p className="font-sans text-[11px] text-[#121212] leading-tight mt-2 px-0.5" data-id="element-1967" data-mp="2aivwjg0dvyg4">{t('Bac endommagé', 'Damaged bin')}</p>
              <p className="font-sans text-[10.5px] text-[#8A90A0] leading-tight mt-0.5 px-0.5 pb-1" data-id="element-1968" data-mp="280acm40gk8j6">
                {t('123, rue Principale', '123 Principale St.')}
              </p>
            </div>
          </div>
          <span className={`${actionBase} border-[#4F46E5] text-[#4F46E5] justify-center`} data-id="element-1969" data-mp="1y1l4h2yd62g4">
            <ExternalLinkIcon className="w-4 h-4" strokeWidth={1.7} aria-hidden="true" data-id="element-1970" data-mp="1t3t2th7x31yq" />
            <span data-id="element-1971" data-mp="1p1fxuv0zv830">{t('Suivre ma demande', 'Track my request')}</span>
            <ChevronRightIcon className="w-4 h-4 ml-auto" aria-hidden="true" data-id="element-1972" data-mp="1p3spjzwk0wt9" />
          </span>
        </div>
  }];
  return <div className="max-w-[1440px] mx-auto" data-id="element-1973" data-mp="1fbd5mx1p5y5q">
      <motion.div initial={{
      opacity: 0,
      y: 16
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.5,
      ease: EASE
    }} className="text-center" data-id="element-1974" data-mp="1fcekzdpqx6t9">
        <p className="font-sans text-xs font-semibold tracking-[0.24em] uppercase text-[#4F46E5]" data-id="element-1975" data-mp="11cdhrrm0dnam">
          {t('Une expérience qui s’adapte au contexte', 'An experience that adapts to context')}
        </p>
        <h2 className="font-serif text-[2.2rem] md:text-[2.9rem] lg:text-[3.4rem] text-[#121212] leading-[1.1] tracking-tight mt-6" data-id="element-1976" data-mp="vqmehlvyedc9">
          {t('Une question. La bonne réponse. La bonne action.', 'One question. The right answer. The right action.')}
        </h2>
      </motion.div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-5 gap-y-12 md:gap-y-10" data-id="element-1977" data-mp="1aezagiv1jw6r">
        {journeys.map((j, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.45,
        delay: i * 0.05,
        ease: EASE
      }} className="grid grid-rows-[auto_auto_1fr]" data-id="element-1978" data-mp="1efavhwrbfyir" data-mpi={i}>
            {j.q}
            <Arrow data-id="element-1979" data-mp="1tc0m2mj3oujo" />
            {j.a}
          </motion.div>)}
      </div>

      <div className="mt-10 flex items-center justify-center gap-4 rounded-2xl border border-[#E7E8F0] bg-[#F8F8FD] px-6 py-4" data-id="element-1980" data-mp="1y6o9ei2dbiwo">
        <span className="w-9 h-9 shrink-0 rounded-full bg-white border border-[#E7E8F0] flex items-center justify-center" data-id="element-1981" data-mp="23i1rss7y96f0">
          <LockIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1982" data-mp="2h9gfhccsxvse" />
        </span>
        <p className="font-sans text-[13.5px] text-[#5B6478] leading-relaxed" data-id="element-1983" data-mp="2cb53ow2y5b7j">
          {t('Lorsque l’identité, l’adresse ou les préférences sont déjà connues et autorisées, Collectes ATNA ne redemande que l’information nécessaire.', 'When identity, address or preferences are already known and authorized, ATNA Waste Collection only asks for the information it needs.')}
        </p>
      </div>
    </div>;
}